import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getPageBySlug } from '../api/pagesApi';
import { getSectionsByPageId } from '../api/sectionsApi';
import SectionEditor from '../features/cms/sections/SectionEditor';
import { getPageEditorBackPath } from '../utils/pageGroups';

const SECTION_LABELS = {
  hero: 'Hero',
  approach: 'Approach',
  earn: 'Earn',
  'core-parameters': 'Core Parameters',
  savings: 'Savings',
  'account-features': 'Account Features',
  growth: 'Growth',
  products: 'Products',
  security: 'Security',
  'how-to-get-started': 'How to Get Started',
  'build-your-heritage': 'Build Your Heritage',
  protocols: 'Protocols',
  operational: 'Operational',
  global: 'Global',
  'never-static': 'Never Static',
  'bank-on': 'Bank On',
  did: 'DID',
  engineered: 'Engineered',
  metatrader: 'MetaTrader',
  'entry-points': 'Entry Points',
  awaits: 'Awaits',
  dedicated: 'Dedicated',
  packages: 'Packages',
  'why-clients': 'Why Clients',
  included: 'Included',
  'request-to-response': 'Request to Response',
  'access-control': 'Access Control',
  faq: 'FAQ',
  continuity: 'Continuity',
  posts: 'Posts',
  post: 'Post',
  content: 'Content',
  'market-insights': 'Market Insights',
  'insight-to-strategy': 'Insight to Strategy',
  pillars: 'Pillars',
  featured: 'Featured',
  news: 'News',
  subscribe: 'Subscribe',
  heritage: 'Heritage',
  'event-impact': 'Event Impact',
  'simple-workflow': 'Simple Workflow',
  'macro-highlights': 'Macro Highlights',
  'behind-the-data': 'Behind the Data',
  form: 'Form',
  speak: 'Speak',
  partner: 'Looking for Partner',
  view: 'View',
  'what-we-deliver': 'What We Deliver',
  'why-choose': 'Why Choose',
  'how-we-work': 'How We Work',
  'time-is-valuable': 'Time Is Valuable',
  'global-companies': 'Global Companies',
  'market-segments': 'Market Segments',
  'featured-companies': 'Featured Companies',
  'market-access': 'Market Access',
  risk: 'Risk',
  start: 'Start',
  'our-expertise': 'Our Expertise',
  'tailored-strategies': 'Tailored Strategies',
  'our-approach': 'Our Approach',
  'who-we-work-with': 'Who We Work With',
  advantage: 'The VBS Advantage',
  'lets-build': "Let's Build",
  ttg: 'Tailored · Trusted · Global',
  'what-we-do': 'What We Do',
  'global-markets': 'Global Markets',
  'wealth-savings': 'Wealth & Savings',
  markets: 'Markets',
  ready: 'Ready',
};

const getSectionLabel = (section) => SECTION_LABELS[section.key] || section.name;

const PageEditorPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [sections, setSections] = useState([]);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadPage = async (keepSelection = true, options = {}) => {
    const { silent = false } = options;

    if (!silent) {
      setIsLoading(true);
    }
    setError('');

    try {
      const loadedPage = await getPageBySlug(slug);
      if (!loadedPage) {
        throw new Error('Page was not found.');
      }

      const loadedSections = await getSectionsByPageId(loadedPage.id);
      setPage(loadedPage);
      setSections(loadedSections);

      if (keepSelection && selectedSectionId) {
        const stillExists = loadedSections.some((section) => section.id === selectedSectionId);
        if (!stillExists && loadedSections.length > 0) {
          setSelectedSectionId(loadedSections[0].id);
        }
      } else if (loadedSections.length > 0) {
        setSelectedSectionId(loadedSections[0].id);
      } else {
        setSelectedSectionId(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load page.');
      setPage(null);
      setSections([]);
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    loadPage(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const selectedSection = useMemo(
    () => sections.find((section) => section.id === selectedSectionId) || null,
    [sections, selectedSectionId]
  );

  const backPath = getPageEditorBackPath(slug, page?.description);
  const backLabel = backPath === '/dashboard/pages' ? 'All pages' : 'Solution pages';

  if (isLoading) {
    return <p className="text-sm text-[#6b635c]">Loading page…</p>;
  }

  if (error) {
    return (
      <div className="space-y-4">
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
        <Link
          to={backPath}
          className="inline-flex text-sm font-medium text-[#785E32] hover:underline"
        >
          Back to pages
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl">
      <header className="mb-6 border-b border-[#e4d9c8] pb-5">
        <Link
          to={backPath}
          className="text-[12px] font-medium text-[#785E32] hover:underline"
        >
          ← {backLabel}
        </Link>
        <p className="mt-3 text-[12px] font-medium uppercase tracking-[0.16em] text-[#6b635c]">
          {page?.name}
        </p>
        <h2 className="mt-2 font-[Merriweather] text-[1.5rem] text-[#130705]">
          Page sections
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#6b635c]">
          Choose a section to edit. Changes apply to the public {page?.name?.toLowerCase()} page.
        </p>
      </header>

      <div className="grid gap-6 xl:grid-cols-[240px_minmax(0,1fr)]">
        <aside className="rounded-md border border-[#e4d9c8] bg-white p-3">
          <p className="mb-2 px-2 text-[11px] font-medium uppercase tracking-[0.14em] text-[#a8a098]">
            Sections
          </p>
          {sections.length === 0 ? (
            <p className="px-2 text-[13px] text-[#6b635c]">No sections yet.</p>
          ) : (
            <ul className="space-y-1">
              {sections.map((section) => {
                const isActive = selectedSectionId === section.id;

                return (
                  <li key={section.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedSectionId(section.id)}
                      className={`w-full rounded-md border px-3 py-2.5 text-left transition !shadow-none hover:!translate-y-0 hover:!shadow-none ${
                        isActive
                          ? '!border-[#d4d4d8] !bg-[#f4f4f5] font-medium !text-[#130705]'
                          : '!border-transparent !bg-white !text-[#4a4340] hover:!border-[#e4e4e7] hover:!bg-[#fafafa] hover:!text-[#130705]'
                      }`}
                    >
                      <span className="block text-[13px]">{getSectionLabel(section)}</span>
                      <span className="mt-0.5 block text-[11px] text-[#a8a098]">{section.key}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        <div className="min-w-0 rounded-md border border-[#e4d9c8] bg-white p-5 sm:p-6">
          {selectedSection ? (
            <SectionEditor
              section={selectedSection}
              pageSlug={slug}
              pageDescription={page?.description}
              pageId={page?.id}
              onSaved={() => loadPage(true, { silent: true })}
            />
          ) : (
            <p className="text-sm text-[#6b635c]">Select a section to edit.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PageEditorPage;
